import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Wallet, TrendingUp, MapPin, Building, FileText, Mail as MailIcon, Users, CheckCircle2, Tag as TagIcon, X, Plus } from "lucide-react";
import { api, API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { StatusBadge } from "../components/Layout";
import { Skeleton } from "../components/ui/skeleton";
import { Input } from "../components/ui/input";

const STAGE_LABELS = { lead: "Lead", analysis: "Analisi", contact: "Contatto", documentation: "Documenti", proposal: "Proposta", negotiation: "Trattativa", agreement: "Accordo", closed: "Chiuso" };

export default function InvestorDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");

  const load = () => api.get(`/contacts/${id}/dashboard`).then(({ data }) => { setData(data); setTags(data.contact.tags || []); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveTags = async (next) => {
    try {
      await api.patch(`/contacts/${id}/tags`, { tags: next });
      setTags(next); toast.success("Tag aggiornati");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const addTag = () => {
    const t = newTag.trim();
    if (!t || tags.includes(t)) { setNewTag(""); return; }
    const next = [...tags, t]; saveTags(next); setNewTag("");
  };
  const removeTag = (t) => saveTags(tags.filter((x) => x !== t));

  if (!data) return <Skeleton className="h-96 w-full" />;
  const c = data.contact;
  const a = data.aggregates;

  return (
    <div className="space-y-6" data-testid="investor-detail">
      <Link to="/investors" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Torna agli investitori
      </Link>

      {/* Header card */}
      <Card className="border-border/60 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight">{c.name}</h1>
            <div className="text-sm text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
              {c.company && <span className="inline-flex items-center gap-1"><Building className="w-3.5 h-3.5" />{c.company}</span>}
              {c.email && <span className="inline-flex items-center gap-1"><MailIcon className="w-3.5 h-3.5" />{c.email}</span>}
              {c.phone && <span className="inline-flex items-center gap-1">📞 {c.phone}</span>}
            </div>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700 border-transparent capitalize text-sm px-3 py-1">{c.role}</Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <Stat icon={Wallet} label="Budget" value={c.investor_budget_max ? `€ ${fmt(c.investor_budget_min || 0)} – ${fmt(c.investor_budget_max)}` : "—"} />
          <Stat icon={TrendingUp} label="ROI minimo" value={c.investor_roi_min ? `${c.investor_roi_min}%` : "—"} />
          <Stat icon={Wallet} label="Liquidità" value={c.investor_liquidity ? `€ ${fmt(c.investor_liquidity)}` : "—"} />
          <Stat icon={CheckCircle2} label="Operazioni chiuse" value={`${a.closed_deals_count} · € ${fmt(a.total_closed_value)}`} />
        </div>
        {(c.investor_cities?.length || c.investor_types?.length) > 0 && (
          <div className="mt-4 pt-4 border-t border-border/60 flex flex-wrap gap-4">
            {c.investor_cities?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" />Zone preferite</div>
                <div className="flex flex-wrap gap-1">{c.investor_cities.map((x) => <Badge key={x} variant="secondary" className="text-xs">{x}</Badge>)}</div>
              </div>
            )}
            {c.investor_types?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Tipologie</div>
                <div className="flex flex-wrap gap-1">{c.investor_types.map((x) => <Badge key={x} variant="secondary" className="text-xs capitalize">{x}</Badge>)}</div>
              </div>
            )}
          </div>
        )}
        {c.notes && <p className="text-sm text-muted-foreground mt-4 pt-4 border-t border-border/60">{c.notes}</p>}

        {/* Custom Investor Tags */}
        <div className="mt-4 pt-4 border-t border-border/60">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><TagIcon className="w-3 h-3" />Tag privati investitore</div>
          <div className="flex flex-wrap gap-1.5 items-center">
            {tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary/10 text-primary" data-testid={`tag-chip-${t}`}>
                {t}
                <button onClick={() => removeTag(t)} className="hover:text-destructive" data-testid={`del-tag-${t}`}><X className="w-3 h-3" /></button>
              </span>
            ))}
            <div className="inline-flex items-center gap-1">
              <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTag()}
                placeholder="es. Decisionale rapido" className="h-7 text-xs w-48" data-testid="new-tag-input" />
              <button onClick={addTag} className="w-7 h-7 rounded-md border border-border/60 hover:bg-primary hover:text-primary-foreground hover:border-primary flex items-center justify-center" data-testid="add-tag-button">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Matched opportunities */}
      <Card className="border-border/60 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h3 className="font-heading text-lg font-medium">Opportunità consigliate</h3>
          <Badge variant="secondary" className="ml-auto">{data.matched_properties.length}</Badge>
        </div>
        {data.matched_properties.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Nessuna corrispondenza al momento.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.matched_properties.slice(0, 6).map((m) => (
              <Link key={m.property.id} to={`/properties/${m.property.id}`} className="border border-border/60 rounded-md p-3 hover:bg-muted/30 transition-colors" data-testid={`inv-match-${m.property.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{m.property.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{m.property.city}</div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 border-transparent tabular-nums shrink-0">{m.match_score}</Badge>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs">
                  <span className="text-muted-foreground">€ {fmt(m.property.price)}</span>
                  <span className="text-emerald-600 font-medium">ROI {m.roi}%</span>
                  <span className="ml-auto text-muted-foreground">Score: {m.score}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {m.reasons.slice(0, 3).map((r, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">{r}</span>)}
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      {/* Deals + Communications side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border/60 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-lg font-medium">Trattative</h3>
            <Badge variant="secondary" className="ml-auto">{data.deals.length}</Badge>
          </div>
          {data.deals.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Nessuna trattativa.</p> : (
            <div className="space-y-2">
              {data.deals.slice(0, 8).map((d) => (
                <div key={d.id} className="flex items-center justify-between p-2.5 border border-border/60 rounded-md text-sm" data-testid={`inv-deal-${d.id}`}>
                  <div className="min-w-0"><div className="font-medium truncate">{d.title}</div><div className="text-xs text-muted-foreground tabular-nums">€ {fmt(d.value)}</div></div>
                  <Badge variant="secondary" className="text-xs shrink-0">{STAGE_LABELS[d.stage] || d.stage}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="border-border/60 p-6">
          <div className="flex items-center gap-2 mb-4">
            <MailIcon className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-lg font-medium">Storico contatti</h3>
            <Badge variant="secondary" className="ml-auto">{data.communications.length}</Badge>
          </div>
          {data.communications.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Nessuna comunicazione.</p> : (
            <div className="space-y-2 max-h-80 overflow-y-auto scrollbar-thin">
              {data.communications.map((c) => (
                <div key={c.id} className="p-2.5 border border-border/60 rounded-md text-sm">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="capitalize">{c.medium} · {c.direction === "out" ? "→" : "←"}</span>
                    <span className="tabular-nums">{new Date(c.sent_at).toLocaleDateString("it-IT")}</span>
                  </div>
                  <div className="font-medium mt-1 truncate">{c.subject}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Shared documents */}
      {data.shared_documents.length > 0 && (
        <Card className="border-border/60 p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-lg font-medium">Documenti condivisi</h3>
            <Badge variant="secondary" className="ml-auto">{data.shared_documents.length}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.shared_documents.slice(0, 10).map((f) => (
              <a key={f.id} href={`${API}/files/${f.storage_path}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2 border border-border/60 rounded-md hover:bg-muted/40 text-sm">
                <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="flex-1 truncate">{f.original_filename}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{Math.round((f.size || 0) / 1024)} KB</span>
              </a>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
function fmt(n) { return Intl.NumberFormat("it-IT").format(Math.round(n || 0)); }
function Stat({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Icon className="w-3 h-3" />{label}</div>
      <div className="font-heading font-semibold text-lg tabular-nums mt-1">{value}</div>
    </div>
  );
}
